import React, { useState } from "react";
import { ImagePath } from "../../../assests";
import { useNavigate } from "react-router-dom";
import TextInputComp from "../../../components/customer/TextInputComp";
import CommonButton from "../../../components/CommonButton";
import { validateEmail } from "../../../utils/Customer/validations";
import { useAdminLoginMutation } from "../../../redux/actions/Admin/authAdmin";
import { useAlert } from "../../../components/customer/AlertContext";
import { Eye, EyeOff } from "lucide-react";
import { useDispatch } from "react-redux";
import { setAdminLoggedIn } from "../../../redux/reducers/authReducer";

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();

  const navigate = useNavigate();
  const { showConfirm } = useAlert();

  const [adminLogin] = useAdminLoginMutation();

  const handleLoginClick = async () => {
    let valid = true;

    if (!email) {
      setEmailError("Email is required");
      valid = false;
    }

    if (!password) {
      setPasswordError("Password is required");
      valid = false;
    }

    const validationError = validateEmail(email);

    if (validationError) {
      setEmailError(validationError);
      valid = false;
    }

    if (!valid) return;

    setEmailError(null);
    setPasswordError(null);

    try {
      setLoading(true);

      const response = await adminLogin({
        email,
        password,
        clientType: "1",
        webName: "chrome",
        deviceId: crypto?.randomUUID?.() ?? Math.random().toString(36),
      }).unwrap();

      console.log("Admin login response:", response);

      if (response?.success) {
        dispatch(
          setAdminLoggedIn({
            userId: "",
            userName: "Admin",
          }),
        );

        navigate("/admin/admindashboard");
        return;
      }

      showConfirm({
        title: "Error",
        message: response?.Message || "Invalid admin credentials.",
        type: "error",
        confirmText: "OK",
      });
    } catch (error: any) {
      console.log("Admin login error:", error);

      const message = error?.data?.Message || "Invalid admin credentials.";

      showConfirm({
        title: "Login Failed",
        message,
        type: "error",
        confirmText: "OK",
      });
    } finally {
      setLoading(false);
    }
  };

  // const handleLoginClick = async () => {
  //     let valid = true;

  //     if (!email) {
  //         setEmailError("Email is required");
  //         valid = false;
  //     }

  //     if (!password) {
  //         setPasswordError("Password is required");
  //         valid = false;
  //     }

  //     const validationError = validateEmail(email);
  //     if (validationError) {
  //         setEmailError(validationError);
  //         valid = false;
  //     }

  //     if (!valid) return;

  //     setEmailError(null);
  //     setPasswordError(null);

  //     try {
  //         setLoading(true);

  //         showConfirm({
  //             title: "OTP Sent",
  //             message: "Login successfully",
  //             type: "success",
  //             confirmText: "Continue",
  //             onConfirm: () => {
  //                 navigate('/admin/admindashboard');
  //             },
  //         });

  //     } catch (error) {
  //         showConfirm({
  //             title: "Error",
  //             message: "Something went wrong",
  //             type: "error",
  //             confirmText: "OK",
  //         });
  //     } finally {
  //         setLoading(false);
  //     }
  // };

  return (
    <div className="h-screen bg-[#F4F4F2] flex items-center justify-center px-4">
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col md:flex-row h-[400px]">
        {/* LEFT SIDE */}
        <div className="w-full md:w-1/2 px-8 bg-white py-4">
          <div className="mb-4 w-full flex justify-start">
            <img
              src={ImagePath.Logo}
              alt="Logo"
              className="h-7 object-contain"
            />
          </div>

          <div className="flex flex-col justify-center items-center">
            <h2 className="text-2xl font-bold text-[#2F5E2E] mb-1">
              Admin Login
            </h2>

            <p className="text-gray-500 mb-4">
              Welcome back! Please login to continue
            </p>
          </div>

          {/* Email */}
          <div className="mb-2">
            <TextInputComp
              type="email"
              placeholder="Email"
              value={email}
              onChangeText={(text) => {
                setEmail(text);
                setEmailError(null);
              }}
              className="py-3"
              error={emailError}
            />
            {emailError && <p className="text-sm text-red-500">{emailError}</p>}
          </div>

          {/* Password */}
          <div className="mb-2 relative">
            <TextInputComp
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChangeText={(text) => {
                setPassword(text);
                setPasswordError(null);
              }}
              className="py-3 pr-10"
              error={passwordError}
            />

            {/* Eye Icon */}
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
            </button>

            {passwordError && (
              <p className="text-sm text-red-500">{passwordError}</p>
            )}
          </div>

          {/* Forgot */}
          <div className="flex justify-center items-center mb-3 text-sm">
            <button className="text-[#6DBE45] hover:text-[#5DAF3C]">
              Forgot password?
            </button>
          </div>

          {/* Button */}
          <CommonButton
            text={loading ? "Logging in..." : "Login"}
            onClick={handleLoginClick}
            className="w-full"
            variant="filled"
            rounded="md"
            disabled={loading}
          />
        </div>

        {/* RIGHT IMAGE */}
        <div className="hidden md:flex w-1/2 bg-[#F4F4F2]">
          <img
            src="https://thumbs.dreamstime.com/b/happy-dog-cat-blue-background-animals-love-each-other-312712586.jpg"
            alt="Dog and Cat"
            className="w-full h-full object-cover"
          />
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
